<template>
    <div id="quick-responses">
        <div class="d-flex justify-content-between align-items-center">
            <a-input-search v-model="searchText" :placeholder="$t('message.startFilter')" class="search-input" />
            <div class="d-flex align-items-center">
                <supsis-button
                    primary
                    style="margin-right: 8px"
                    @click="openNewTitleFormModal"
                    v-if="checkPermission(PERMISSION_TYPES['prepared.create'].tr.name)"
                >
                    <md-icon style="color: white">add</md-icon>
                    {{ $t("message.newTitleContent") }}
                </supsis-button>
                <supsis-button primary @click="openNewContentFormModal">
                    <md-icon style="color: white">add</md-icon>
                    {{ $t("message.newDefaultContent") }}
                </supsis-button>
            </div>
        </div>
        <b-container fluid>
            <draggable class="row m-1" v-model="contentOrdered">
                <b-col v-for="(content, index) in cloneGrouped" :key="index" class="mt-5" md="4">
                    <div class="custom-card">
                        <div class="header">
                            <div class="hoverTools">
                                <a-button
                                    class="mx-2"
                                    icon="plus"
                                    size="small"
                                    type="primary"
                                    @click="openAddContentToTitleFormModal(content, CONTENT_TYPES.ALT)"
                                >
                                    {{ $t(isMac ? "message.newCTRLContent" : "message.newALTContent") }}
                                </a-button>
                                <a-button
                                    class="mx-2"
                                    icon="plus"
                                    size="small"
                                    type="primary"
                                    @click="openAddContentToTitleFormModal(content, CONTENT_TYPES.TAB)"
                                >
                                    {{ $t("message.newTABContent") }}
                                </a-button>
                                <a-button
                                    class="mx-2"
                                    icon="plus"
                                    size="small"
                                    type="primary"
                                    @click="openAddContentToTitleFormModal(content, CONTENT_TYPES.HASH)"
                                >
                                    {{ $t("message.newHASHContent") }}
                                </a-button>
                            </div>
                            <span>{{ getContentName(content) }}</span>
                            <div class="actions">
                                <a-button class="actionButton d-flex align-items-center" @click="edit(content._id)">
                                    <span style="margin-right: 10px; display: block">{{ $t("message.edit") }}</span>
                                    <ActionEdit />
                                </a-button>
                                <a-button
                                    v-if="!content.hasOwnProperty('children') || content.children.length === 0"
                                    class="actionButton d-flex align-items-center"
                                    @click="remove(content._id)"
                                >
                                    <span style="margin-right: 10px; display: block">{{
                                        $t("message.deleteField")
                                    }}</span>
                                    <ActionDelete />
                                </a-button>
                            </div>
                        </div>
                        <div style="overflow-y: auto">
                            <draggable
                                :id="content._id"
                                :key="content._id"
                                :move="move"
                                :value="getChildren(content)"
                                class="body"
                                group="contents"
                                @end="moveEnd"
                            >
                                <div
                                    v-for="child in getChildren(content)"
                                    :id="child._id"
                                    :key="child._id"
                                    class="item"
                                >
                                    <div
                                        v-tippy="{
                                            followCursor: true,
                                            placement: 'right',
                                            theme: isDarkMode ? 'light-border' : 'google',
                                        }"
                                        :content="getContentName(child, 'content')"
                                        class="d-flex align-items-center"
                                        style="cursor: help; width: calc(100% - 100px)"
                                    >
                                        <div class="shortcuts">
                                            <div v-if="child.type === 'ALT'" class="shortcut">
                                                {{ isMac ? "CTRL" : child.type }}
                                                +
                                                {{ child.letter }}
                                            </div>
                                            <div v-else-if="child.type === 'TAB'" class="shortcut">
                                                {{ child.letter }}
                                                <right-button />
                                                {{ child.type }}
                                                <right-button />
                                                {{ child.number }}
                                            </div>
                                            <div v-else-if="child.type === 'HASH'" class="shortcut">
                                                #{{ child.hash }}
                                            </div>
                                        </div>
                                        <div class="contentInfo">
                                            {{ getContentName(child) }}
                                        </div>
                                    </div>
                                    <div class="actions">
                                        <a-button
                                            class="actionButton d-flex align-items-center"
                                            @click="edit(child._id, true)"
                                        >
                                            <ActionEdit />
                                        </a-button>
                                        <a-button
                                            class="actionButton d-flex align-items-center"
                                            @click="remove(child._id)"
                                            v-if="checkPermission(PERMISSION_TYPES['prepared.delete'].tr.name)"
                                        >
                                            <ActionDelete />
                                        </a-button>
                                    </div>
                                </div>
                            </draggable>
                        </div>
                    </div>
                </b-col>
            </draggable>
        </b-container>
        <Modal2
            v-if="isModalShow"
            id="content-form"
            :show-footer="false"
            @cancel="hideModal"
            :title="editMode ? $t('message.updateDefaultContentForm') : $t('message.newDefaultContentForm')"
        >
            <div id="modal-inner">
                <a-alert
                    v-if="$validator.errors.items.length && submitClicked"
                    :message="$t('message.error')"
                    class="mb-2"
                    showIcon
                    type="error"
                >
                    <div slot="description">
                        <div v-for="(err, index) in $validator.errors.items" :key="'err-' + index">
                            <span>
                                {{ err.msg }}
                            </span>
                        </div>
                    </div>
                </a-alert>
                <b-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                    <b-row>
                        <b-col md="12">
                            <b-row>
                                <b-col v-if="isChild" md="12">
                                    <b-form-group :label="$t('message.contentType')" label-for="type-input">
                                        <b-form-select
                                            id="type-input"
                                            v-model="content.type"
                                            v-validate="'required'"
                                            :data-vv-as="$t('message.contentType')"
                                            :name="$t('message.contentType')"
                                            type="text"
                                        >
                                            <option :value="undefined">{{ $t("message.pickOne") }}...</option>
                                            <option value="ALT">
                                                {{ isMac ? "CTRL" : "ALT" }}
                                            </option>
                                            <option value="TAB">TAB</option>
                                            <option value="HASH">#HASH</option>
                                            <option value="TITLE">
                                                {{ $t("message.title") }}
                                            </option>
                                        </b-form-select>
                                    </b-form-group>
                                </b-col>
                                <b-col v-if="(content.type || '').toLowerCase() !== 'title'" md="12">
                                    <b-form-group id="type-input-group" label="Grup" label-for="type-input">
                                        <b-form-select
                                            id="type-input"
                                            v-model="content.parentId"
                                            :options="
                                                contents.data
                                                    .filter((c) => c.parentId === null)
                                                    .map((c) => ({
                                                        value: c._id,
                                                        text: getContentName(c),
                                                    }))
                                            "
                                            name="type"
                                            type="text"
                                        >
                                        </b-form-select>
                                    </b-form-group>
                                </b-col>
                                <b-col md="12">
                                    <b-form-group
                                        v-if="(content.type || '').toLowerCase() === 'alt'"
                                        id="letter-input-group"
                                        :label="$t('message.shortcutChar')"
                                        label-for="letter-input"
                                    >
                                        <b-form-input
                                            id="letter-input"
                                            v-model="content.letter"
                                            v-validate="'required|alpha_num|length:1|excluded:e,d,f,z,a,x,c,v,w,t,n'"
                                            :data-vv-as="$t(content.letter)"
                                            :name="$t('message.shortcutChar')"
                                            :placeholder="$t('message.writeSomething')"
                                            autocomplete="off"
                                            trim
                                            type="text"
                                        />
                                        <!--<span class="text-danger mt-3">{{ errors.first('letter') }}</span>-->
                                        <em class="font-weight-light"
                                            >{{ $t("message.shortcut") }}: alt + {{ content.letter }}</em
                                        >
                                        <em class="d-block font-weight-light">{{ $t("message.forMacUsers") }}</em>
                                        <em class="d-block font-weight-light text-danger">{{
                                            $t("message.notValid")
                                        }}</em>
                                    </b-form-group>
                                </b-col>
                                <b-col md="12">
                                    <div>
                                        <b-row
                                            v-if="(content.type || '').toLowerCase() === 'tab'"
                                            class="align-items-center"
                                        >
                                            <b-col md="5">
                                                <b-form-group
                                                    :label="$t('message.letterSequence')"
                                                    label-for="letter-input"
                                                >
                                                    <b-form-input
                                                        id="letter-input"
                                                        v-model="content.letter"
                                                        v-validate="'required'"
                                                        :data-vv-as="$t('message.letterSequence')"
                                                        :name="$t('message.letterSequence')"
                                                        :placeholder="$t('message.exampleText')"
                                                        autocomplete="off"
                                                        trim
                                                        type="text"
                                                    />
                                                    <!--<span class="text-danger mt-3">{{ errors.first('letter') }}</span>-->
                                                </b-form-group>
                                            </b-col>
                                            <b-col class="text-center" md="2">
                                                <b-badge class="mt-2">TAB</b-badge>
                                            </b-col>
                                            <b-col md="5">
                                                <b-form-group
                                                    id="number-input-group"
                                                    :label="$t('message.shorcutKey')"
                                                    label-for="number-input"
                                                >
                                                    <b-form-input
                                                        id="number-input"
                                                        v-model="content.number"
                                                        v-validate="'required|length:1'"
                                                        :data-vv-as="$t('message.shorcutKey')"
                                                        :name="$t('message.shorcutKey')"
                                                        :placeholder="$t('message.anyChar')"
                                                        autocomplete="off"
                                                        trim
                                                    />
                                                    <!--<span class="text-danger mt-3">{{ errors.first('number') }}</span>-->
                                                </b-form-group>
                                            </b-col>
                                        </b-row>
                                        <MultiContent ref="MultiContent" v-model="multiContentComp" />
                                    </div>
                                </b-col>
                                <b-col md="12">
                                    <b-form-group
                                        v-if="(content.type || '').toLowerCase() === 'hash'"
                                        id="hashText"
                                        :label="$t('message.hashText')"
                                        label-for="hashText-input"
                                    >
                                        <b-input-group prepend="#" class="mb-2 mr-sm-2 mb-sm-0">
                                            <b-form-input
                                                id="hashText-input"
                                                v-model="content.hash"
                                                v-validate="'required|alpha_num'"
                                                :data-vv-as="$t(content.hash)"
                                                :name="$t('message.hashText')"
                                                :placeholder="$t('message.exampleHash')"
                                                autocomplete="off"
                                                trim
                                                type="text"
                                            />
                                        </b-input-group>

                                        <!--<span class="text-danger mt-3">{{ errors.first('letter') }}</span>-->
                                        <!--                                    <em class="font-weight-light">{{ $t("message.hash") }}: #{{ content.hash }}</em>-->
                                        <em class="d-block font-weight-light text-danger">{{
                                            $t("message.notValidHash")
                                        }}</em>
                                    </b-form-group>
                                </b-col>
                                <b-col md="12">
                                    <b-form-group label="Durum">
                                        <b-form-radio-group v-model="content.isActive" name="status" required>
                                            <b-form-radio :value="true">{{ $t("message.active") }}</b-form-radio>
                                            <b-form-radio :value="false">{{ $t("message.passive") }}</b-form-radio>
                                        </b-form-radio-group>
                                    </b-form-group>
                                </b-col>
                                <b-col md="12" id="buttons">
                                    <b-button @click="hideModal">
                                        <template>
                                            {{ $t("message.cancel") }}
                                        </template>
                                    </b-button>
                                    <b-button :disabled="loading" type="submit" variant="primary">
                                        <template v-if="!loading">
                                            <span v-if="!editMode">
                                                {{ $t("message.save") }}
                                            </span>
                                            <span v-else>
                                                {{ $t("message.update") }}
                                            </span>
                                        </template>
                                        <template v-else>
                                            <span v-if="checkPermission(PERMISSION_TYPES['prepared.update'].tr.name)">
                                                <font-awesome-icon icon="spinner" spin />
                                            </span>
                                        </template>
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </b-form>
            </div>
        </Modal2>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../../../components/common/AppBreadCrumb.vue";
import { log2 } from "../../../../utils/log2";
import * as _ from "lodash";
import { CONTENT_TYPES } from "../../../../utils/staticDatas";
import { sendSocketMessage } from "../../../../utils/socketStream";
import ActionDelete from "../../../../components/svg/ActionDelete.vue";
import ActionEdit from "../../../../components/svg/ActionEdit.vue";
import RightButton from "../../../../components/svg/RightButton.vue";
import { isMac } from "../../../../utils/utils";
import MultiContent from "../../../../components/MultiContent.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";
import SupsisButton from "../../Settings/components/SupsisButton.vue";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);
const log = log2("Contents");
const contentInitialObject = {
    _id: null,
    parentId: null,
    type: undefined,
    letter: "",
    number: "",
    isActive: true,
    lastTargetId: undefined,
    languageContent: {},
};

export default {
    name: "Contents",
    components: {
        SupsisButton,
        MultiContent,
        draggable,
        AppBreadCrumb,
        ActionDelete,
        ActionEdit,
        RightButton,
        Modal2,
    },
    data() {
        return {
            searchText: "",
            filteredData: [],
            index: null,
            isBusy: true,
            isChild: false,
            loading: false,
            editMode: false,
            submitClicked: false,
            CONTENT_TYPES,
            content: _.cloneDeep(contentInitialObject),
            perPage: 1000,
            page: 1,
            isMac,
            mentions: [
                { text: this.$t("message.name"), value: "name" },
                { text: this.$t("message.email"), value: "email" },
            ],
            showMentionList: false,
            mentionIndex: 0,
            selectedLang: null,
            contentTitle: "",
            cloneGrouped: [],
            isModalShow: false,

            PERMISSION_TYPES,
        };
    },
    computed: {
        ...mapState({
            contents: (state) => state.content.contents,
            languages: (state) => state.language.languages,
            loggedUser: (state) => state.auth.user,
            isAdmin: (state) => state.auth.adminUser != null,
            site: (state) => state.site.site,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
        }),
        ...mapGetters({
            grouped: "content/getGrouped",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        contentOrdered: {
            get() {
                return this.grouped;
            },
            set(e) {
                let orderUpdated = _.clone(e).map((x, i) => ({ ...x, order: i }));
                let diffs = _.differenceWith(orderUpdated, this.grouped, _.isEqual);
                diffs.forEach((x) => {
                    this.updateContent(x);
                });
                sendSocketMessage("user content-moved", {
                    content: diffs,
                    siteId: this.site._id,
                });
            },
        },
        multiContentComp: {
            get() {
                log("systemLanguages", this.systemLanguages);
                return [
                    {
                        type: "input",
                        field: "name",
                        label: this.$t("message.titleContentForm"),
                        placeholder: this.$t("message.titleContentForm"),
                        data: (this.systemLanguages || []).reduce((acc, lang) => {
                            acc[lang] = _.get(this.content.languageContent, `[${lang}].name`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                    {
                        type: "rich",
                        field: "content",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: (this.systemLanguages || []).reduce((acc, lang) => {
                            acc[lang] = _.get(this.content.languageContent, `[${lang}].content`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.content.languageContent = (this.systemLanguages || []).reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
    },
    methods: {
        ...mapActions({
            getContent: "content/getContent",
            createContent: "content/createContent",
            updateContent: "content/updateContent",
            updateParent: "content/updateParent",
            deleteContent: "content/deleteContent",
            getContents: "content/getContents",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        getContentName(child, readProperty = "name") {
            return (
                _.get(child, `languageContent.${this.$i18n.locale}.${readProperty}`) ||
                _.get(child, `languageContent.${Object.keys(child.languageContent)[0]}.${readProperty}`) ||
                ""
            );
        },
        openAddContentToTitleFormModal(parentContent, contentType) {
            this.editMode = false;
            this.isChild = false;
            this.content = {
                ..._.cloneDeep(contentInitialObject),
                type: contentType,
                parentId: parentContent._id,
            };
            /*this.$bvModal.show("content-form");*/
            this.showModal();
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        openNewTitleFormModal() {
            this.editMode = false;
            this.isChild = false;
            this.content = {
                ..._.cloneDeep(contentInitialObject),
                type: CONTENT_TYPES.TITLE,
            };
            /*this.$bvModal.show("content-form");*/
            this.showModal();
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        openNewContentFormModal() {
            this.editMode = false;
            this.isChild = true;
            /*this.$bvModal.show("content-form");*/
            this.showModal();
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        get() {
            this.isBusy = true;
            return this.getContents({ perPage: this.perPage, page: this.page }).then(() => {
                this.isBusy = false;
                this.cloneGrouped = _.cloneDeep(this.grouped);
            });
        },
        edit(id, isChild = false) {
            this.getContent(id).then((response) => {
                this.editMode = true;
                this.isChild = isChild;
                this.content = _.cloneDeep(response);
                /*this.$bvModal.show("content-form");*/
                this.showModal();
                setTimeout(() => {
                    this.bindListener();
                }, 300);
                setTimeout(() => {
                    this.$validator.validateAll();
                }, 100);
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deleteContentQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteContent({ id }).then((res) => {
                        sendSocketMessage("user content-deleted", {
                            contentId: id,
                            siteId: this.site._id,
                        });
                    });
                    this.$toasted.show(this.$t("message.defaultContentDeleted"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                });
        },
        reset() {
            this.content = _.cloneDeep(contentInitialObject);
            this.submitClicked = false;
        },
        handleSave() {
            log.debug("handleSave");
            this.submitClicked = true;
            this.$validator.validateAll().then((response) => {
                this.loading = true;
                if (this.$validator.errors.items.length) {
                    let Modal = document.getElementById("modal-inner");
                    Modal.scrollTo({
                        top: 0,
                        behavior: "smooth",
                    });
                }
                if (response) {
                    this.$refs.MultiContent.validate().then((isMultiContentValid) => {
                        if (isMultiContentValid) {
                            const result = this.content._id
                                ? this.updateContent(this.content).then((res) => {
                                      sendSocketMessage("user content-updated", {
                                          content: this.content,
                                          siteId: this.site._id,
                                      });
                                  })
                                : this.createContent(this.content).then((res) => {
                                      sendSocketMessage("user new-content-added", {
                                          content: this.content,
                                          siteId: this.site._id,
                                      });
                                  });

                            result
                                .then(() => {
                                    /*this.$bvModal.hide("content-form");*/
                                    this.hideModal();
                                    this.$toasted.show(this.$t("message.defaultContentSaved"), {
                                        duration: 3000,
                                        position: "bottom-center",
                                    });
                                    this.get();
                                    this.reset();
                                })
                                .catch((err) => {
                                    log.error(err.response, err.message);
                                    const errMsg =
                                        err.response && err.response.data && err.response.data.localizedMessages
                                            ? err.response.data.localizedMessages[this.$i18n.locale]
                                            : err.message;
                                    this.$toasted.error(errMsg, {
                                        duration: 4000,
                                        position: "bottom-center",
                                    });
                                })
                                .finally(() => {
                                    this.loading = false;
                                    this.submitClicked = false;
                                });
                        } else {
                            this.loading = false;
                        }
                    });
                } else {
                    this.loading = false;
                }
            });
        },
        async moveEnd(e) {
            try {
                const movingItem = _.find(this.contents.data, (q) => q._id === e.item.id);
                if (movingItem) {
                    const isParent = movingItem.parentId === null;
                    const containsAnySubItem = this.getChildren(movingItem).length > 1;
                    if (isParent && containsAnySubItem) {
                        this.$toasted.error(this.$t("message.cannotMoveFullParent"), {
                            duration: 2000,
                            position: "bottom-center",
                        });
                    } else {
                        await this.updateParent({
                            _id: e.item.id,
                            parentId: this.lastTargetId,
                        }).then(async () => {
                            await this.get();
                            this.cloneGrouped = _.cloneDeep(this.grouped);
                            sendSocketMessage("user content-moved", {
                                content: movingItem,
                                siteId: this.site._id,
                            });
                            this.$toasted.show(this.$t("message.contentMoved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                    }
                }
            } catch (error) {
                console.error("Error moving content:", error);
            }
        },

        move(e) {
            if (e.dragged.id !== e.to.id) {
                const movingItem = _.find(this.contents.data, (q) => q._id === e.dragged.id);
                this.lastTargetId = e.to.id;
                if (movingItem) {
                    const isParent = movingItem.parentId === null;
                    const containsAnySubItem = this.getChildren(movingItem).length > 1;
                    log.debug("xdddd");
                    if (isParent && containsAnySubItem) {
                        return false;
                    } else {
                        return true;
                    }
                }
            }
        },
        getChildren(content) {
            const item = _.cloneDeep(content);
            let items = item.children || [];
            items.push({
                ...item,
                children: undefined,
            });
            items = _.uniqBy(items, (q) => q._id).filter((x) => x.type !== CONTENT_TYPES.TITLE);
            log.debug("getChildren", { item, items });
            return items;
        },
        bindListener() {
            let editors = document.querySelectorAll(".editr div[contenteditable=true]");

            let eventHandler = (e) => (event) => {
                let range = window.getSelection().getRangeAt(0);
                switch (event.key) {
                    case "@":
                        let mentions = document.querySelector(".mentions");
                        let modalRect = e.getBoundingClientRect();
                        let curRect = range.getBoundingClientRect();
                        mentions.style.left = `${curRect.left - modalRect.left}px`;
                        mentions.style.top = `${curRect.top - modalRect.top}px`;
                        this.showMentionList = true;
                        break;
                    case "ArrowDown":
                        if (!this.showMentionList) break;
                        this.mentionIndex =
                            this.mentionIndex === this.mentions.length - 1 ? this.mentionIndex : this.mentionIndex + 1;
                        event.preventDefault();
                        break;
                    case "ArrowUp":
                        if (!this.showMentionList) break;
                        this.mentionIndex = this.mentionIndex === 0 ? 0 : this.mentionIndex - 1;
                        event.preventDefault();
                        break;
                    case "Enter":
                        if (!this.showMentionList) break;
                        this.showMentionList = false;
                        let textNode = document.createTextNode(this.mentions[this.mentionIndex].value);
                        range.insertNode(textNode);
                        event.preventDefault();
                        break;
                    default:
                        this.showMentionList = false;
                        break;
                }
            };

            for (let e of editors) {
                e.addEventListener("keydown", eventHandler(e));
            }
        },
        handleFilter() {
            const filterChildren = this.contents.data.filter((d) => !d?.children);
            const result = [];
            if (this.searchText && this.searchText.trim().length > 0) {
                for (const data of filterChildren) {
                    const searchDataNameAndContent = Object.keys(data.languageContent).find(
                        (lc) =>
                            data.languageContent[lc].name.toLowerCase().includes(this.searchText.toLowerCase()) ||
                            data.languageContent[lc].content.toLowerCase().includes(this.searchText.toLowerCase())
                    );
                    if (searchDataNameAndContent) result.push(data);
                }
            }

            const resultHash = result.reduce((a, b) => {
                a[b._id] = true;
                return a;
            }, {});

            this.cloneGrouped = this.grouped.reduce((a, b) => {
                const searchChildren = b.children.filter((d) => resultHash[d._id]);
                if (!result.length || !!searchChildren.length) {
                    a.push({
                        ...b,
                        children: !searchChildren.length ? b.children : searchChildren,
                    });
                }
                return a;
            }, []);
        },
    },
    watch: {
        searchText() {
            this.handleFilter();
        },
        contentOrdered: {
            deep: true,
            handler(updatedData) {
                this.cloneGrouped = updatedData;
            },
        },
    },
    mounted() {
        this.$validator.localize(this.$i18n.locale);
        this.get();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
};
</script>

<style lang="scss" scoped>
.content {
    width: 85%;
    background: #eef0f8;
}

#quick-responses {
    min-height: 90vh;
}
</style>

<style lang="scss">
.custom-tabs .nav.nav-tabs {
    position: fixed;
    top: 92px;
    left: 92px;
    right: 30px;
    z-index: 123;
}

.mentions {
    z-index: 999999;
    background-color: #8787c7;
    color: #ffffff;
    padding: 0;
    margin: 0;
    list-style: none;
    border-radius: 0.6rem;
    display: inline-block;
    overflow: hidden;
    position: absolute;
    top: 70px;
    left: 10px;
    box-shadow: 0 0 3px #00000059;

    li {
        padding: 0.3rem 1.3rem;
        cursor: pointer;
        border-bottom: 1px solid #5a5a85;

        &:last-child {
            border-bottom: none;
        }

        &:hover,
        &.selected {
            background-color: #4f4fab;
        }
    }
}

.new-theme {
    #quick-responses {
        .custom-card {
            background-color: #ffffff;
            border-radius: 6px;
            padding: 10px;
            padding-top: 0;
            position: relative;
            border: 1px solid var(--color-border-default);

            min-height: 350px;
            height: 350px;
            max-height: 350px;

            .header {
                &:hover .hoverTools {
                    opacity: 1;
                    transform: translateY(0);
                }

                padding-top: 10px;
            }

            .hoverTools {
                position: absolute;
                bottom: 100%;
                left: 0;
                right: 0;
                margin: 20px;
                display: flex;
                justify-content: space-around;
                transition: all 0.1s;
                opacity: 0;
                transform: translateY(100%);
            }

            .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid #3699ff;
                padding-bottom: 10px;

                span {
                    display: block;
                    font-family: Poppins;
                    font-size: 16px;
                    font-weight: 300;
                    color: #181c32;
                }

                .actions {
                    display: flex;

                    .actionButton {
                        border: none;
                        box-shadow: none;
                        padding: 0px 10px;
                    }
                }
            }

            .body {
                min-height: 250px;
                height: 250px;
                max-height: 250px;

                .item {
                    height: 30px;
                    margin: 8px 0;
                    border-radius: 6px;

                    cursor: move;

                    display: flex;
                    justify-content: space-between;

                    background-color: #ffffff;

                    .shortcuts {
                        margin-right: 6px;
                    }

                    .shortcut {
                        background-color: #eaecf2;
                        font-family: Poppins;
                        color: #3699ff;
                        font-size: 14px;
                        font-weight: 300;
                        text-align: center;
                        border-radius: 3px;
                        padding: 5px 5px 5px 5px;
                        white-space: nowrap;
                    }

                    .contentInfo {
                        color: #181c32;
                        font-size: 14px;
                        font-weight: 300;
                        padding-top: 5px;
                        display: block;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        padding-left: 5px;
                    }

                    .actions {
                        display: flex;

                        .actionButton {
                            border: none;
                            box-shadow: none;
                            height: 30px;
                        }
                    }
                }
            }
        }
    }
}

.fab {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    background-color: white;
    position: absolute;
    bottom: 2%;
    left: 16%;
    z-index: 9999;
    transition: all 0.3s;
    box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
    transform: scale(1);
    animation: pulse 3s infinite;
    cursor: pointer;
}

#content-form {
    #modal-inner {
        overflow-y: scroll;
        overflow-x: hidden;
        max-height: 70vh;
        padding: 0 20px;
    }

    .ant-modal-wrap {
        max-height: 100vh;
        /*overflow-y: hidden !important;*/
        /*top: -50px;*/
    }

    .ant-modal {
        width: 750px !important;
        padding-bottom: 0;
        top: 40px;
    }

    #buttons {
        display: flex;
        gap: 7px;
        justify-content: flex-end;
        padding-right: 20px;
    }
}

@keyframes pulse {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
    }

    70% {
        transform: scale(1);
        box-shadow: 0 0 0 10px #00000000;
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 #00000000;
    }
}
</style>
